/**
 * Tree Service - Hierarchical data with parent-child relationships
 *
 * Extends CollectionService with:
 * - Automatic parent_id field injection
 * - Cycle detection in beforeCreate/beforeUpdate hooks
 * - Tree-specific operations (getTree, getAncestors, getDescendants, move)
 *
 * @incompatible RedisAdapter - requires foreign key support
 */

import type {
  PaginatedResult,
  ModuleContext,
  EntityServiceHooks,
  FieldDefinition,
  TreeEntityDefinition,
  CollectionEntityDefinition,
  TreeNode
} from '@gzl10/nexus-sdk'
import type { EntityQuery } from '@kernel/runtime/index.js'
import { CollectionService } from './collection.service.js'
import type { ITreeRepository, EntityRepository } from '../repositories/types.js'
import { createRepoFromCtx } from '../repositories/index.js'
import { loadSeedData } from '../helpers/index.js'

export class TreeService<T extends Record<string, unknown> = Record<string, unknown>>
  extends CollectionService<T> {

  /** Original tree entity definition (before conversion to collection) */
  readonly treeDefinition: TreeEntityDefinition

  constructor(ctx: ModuleContext, definition: TreeEntityDefinition, hooks?: EntityServiceHooks<T>, repo?: EntityRepository) {
    // Inject parent_id field if not already defined
    const enhancedDefinition = injectParentIdField(definition)
    // Convert to collection-like definition for base class
    const collectionDef = {
      ...enhancedDefinition,
      type: 'collection' as const,
      labelField: enhancedDefinition.labelField ?? 'name'
    } as CollectionEntityDefinition

    // Build tree repo from the original tree definition (type: 'tree') so
    // the factory creates KnexTreeRepository instead of KnexCollectionRepository.
    // The repo is passed to super to avoid buildRepo() using collectionDef.
    let resolvedRepo: EntityRepository | undefined = repo
    if (!resolvedRepo) {
      try {
        resolvedRepo = createRepoFromCtx(ctx, { ...enhancedDefinition, type: 'tree' as const })
      } catch {
        // Fallback for non-table-backed contexts (tests may inject repo later)
        resolvedRepo = undefined
      }
    }

    super(ctx, collectionDef, hooks, resolvedRepo)

    // Store original tree definition
    this.treeDefinition = definition

    // Validate adapter compatibility at construction time
    this.validateAdapterCompatibility()
  }

  /**
   * Validate that the adapter is compatible with tree entities.
   * Some adapters don't support foreign keys required for tree structure.
   */
  private validateAdapterCompatibility(): void {
    const adapterName = (this.definition as unknown as { adapter?: string }).adapter
    if (adapterName === 'temp' || adapterName === 'redis') {
      throw new Error(
        `TreeEntityDefinition '${this.definition.table}' is incompatible with adapter '${adapterName}'. ` +
        `Tree entities require foreign key support for parent_id relationships.`
      )
    }
  }

  /**
   * Get all tree nodes with optional filtering
   */
  override async findAll(query?: EntityQuery): Promise<PaginatedResult<T>> {
    return this.baseFindAll(query)
  }

  /**
   * Get tree structure as nested hierarchy
   *
   * @param rootId - Optional root node ID. If provided, returns subtree from that node.
   * @param maxDepth - Optional maximum depth to include.
   * @returns Array of root-level TreeNode objects with nested children
   */
  async getTree(rootId?: string, maxDepth?: number): Promise<TreeNode<T>[]> {
    const repo = this.repo as ITreeRepository<T>
    const flatItems = await repo.getTree(rootId, maxDepth)
    const parsed = this.parseJsonFieldsFromArray(flatItems as Record<string, unknown>[]) as T[]
    return this.buildNestedTree(parsed, rootId)
  }

  /**
   * Build a nested tree structure from a flat BFS-ordered array.
   *
   * @param items - Flat list of nodes (all nodes that belong in the tree)
   * @param rootId - Optional root node ID to use as single root
   * @returns Array of root-level TreeNode objects with nested children
   */
  private buildNestedTree(items: T[], rootId?: string): TreeNode<T>[] {
    // Build map of id -> TreeNode
    const map = new Map<string, TreeNode<T>>()
    for (const item of items) {
      map.set(item['id'] as string, { data: item, children: [] })
    }

    // Build tree by linking children to parents
    const roots: TreeNode<T>[] = []
    for (const item of items) {
      const node = map.get(item['id'] as string)!
      const parentId = item['parent_id'] as string | null

      if (!parentId) {
        // Root node (no parent)
        roots.push(node)
      } else if (rootId && item['id'] === rootId) {
        // This is the requested root — add as root
        roots.push(node)
      } else {
        const parent = map.get(parentId)
        if (parent) {
          parent.children.push(node)
        } else {
          // Orphan node (parent doesn't exist) — treat as root
          roots.push(node)
        }
      }
    }

    // If rootId specified, filter to only that subtree
    if (rootId) {
      return map.has(rootId) ? [map.get(rootId)!] : []
    }

    return roots
  }

  /**
   * Truncate tree nodes beyond the specified depth
   *
   * @param nodes - Tree nodes to truncate
   * @param maxDepth - Maximum depth (0 = only roots, 1 = roots + direct children, etc.)
   * @param currentDepth - Current depth level (used in recursion)
   */
  private truncateAtDepth(nodes: TreeNode<T>[], maxDepth: number, currentDepth = 0): TreeNode<T>[] {
    return nodes.map(node => ({
      data: node.data,
      children: currentDepth < maxDepth
        ? this.truncateAtDepth(node.children, maxDepth, currentDepth + 1)
        : []
    }))
  }

  /**
   * Get ancestors of a node (path from node to root)
   *
   * @param id - Node ID to get ancestors for
   * @returns Array of ancestor nodes, ordered from immediate parent to root
   */
  async getAncestors(id: string): Promise<T[]> {
    const repo = this.repo as ITreeRepository<T>
    const raw = await repo.getAncestors(id)
    return this.parseJsonFieldsFromArray(raw as Record<string, unknown>[]) as T[]
  }

  /**
   * Get all descendants of a node (all children recursively)
   *
   * @param id - Node ID to get descendants for
   * @returns Array of all descendant nodes (breadth-first order)
   */
  async getDescendants(id: string): Promise<T[]> {
    const repo = this.repo as ITreeRepository<T>
    const raw = await repo.getDescendants(id)
    return this.parseJsonFieldsFromArray(raw as Record<string, unknown>[]) as T[]
  }

  /**
   * Get direct children of a node
   *
   * @param parentId - Parent node ID, or null for root nodes
   * @returns Array of direct child nodes
   */
  async findChildren(parentId: string | null): Promise<T[]> {
    const repo = this.repo as ITreeRepository<T>
    const raw = await repo.findChildren(parentId)
    return this.parseJsonFieldsFromArray(raw as Record<string, unknown>[]) as T[]
  }

  /**
   * Get root nodes (nodes with no parent)
   *
   * @returns Array of root nodes
   */
  async findRoots(): Promise<T[]> {
    const repo = this.repo as ITreeRepository<T>
    const raw = await repo.findRoots()
    return this.parseJsonFieldsFromArray(raw as Record<string, unknown>[]) as T[]
  }

  /**
   * Move a node to a new parent
   *
   * @param id - Node ID to move
   * @param newParentId - New parent ID, or null to make it a root node
   * @returns Updated node
   * @throws ValidationError if move would create a cycle
   */
  async move(id: string, newParentId: string | null): Promise<T> {
    // Validate no cycle would be created
    if (newParentId) {
      await this.validateNoCycle(id, newParentId)
    }

    return this.update(id, { parent_id: newParentId } as unknown as Partial<T>)
  }

  /**
   * Get the depth of a node in the tree (root = 0)
   *
   * @param id - Node ID
   * @returns Depth level (0 for root nodes)
   */
  async getDepth(id: string): Promise<number> {
    const ancestors = await this.getAncestors(id)
    return ancestors.length
  }

  /**
   * Check if a node is an ancestor of another node
   *
   * @param ancestorId - Potential ancestor node ID
   * @param descendantId - Potential descendant node ID
   * @returns true if ancestorId is an ancestor of descendantId
   */
  async isAncestorOf(ancestorId: string, descendantId: string): Promise<boolean> {
    const ancestors = await this.getAncestors(descendantId)
    return ancestors.some(a => a['id'] === ancestorId)
  }

  // ============================================================================
  // Cycle Detection
  // ============================================================================

  /**
   * Validate that setting parent_id would not create a cycle.
   *
   * A cycle occurs when:
   * - Node becomes its own parent (self-reference)
   * - Node becomes parent of one of its ancestors (circular reference)
   *
   * @param nodeId - Node being modified
   * @param newParentId - Proposed new parent
   * @throws ValidationError if cycle would be created
   */
  private async validateNoCycle(nodeId: string, newParentId: string): Promise<void> {
    // Self-reference check
    if (nodeId === newParentId) {
      throw new this.errors.ValidationError(
        'A node cannot be its own parent',
        [{ path: 'parent_id', message: 'Self-reference not allowed' }]
      )
    }

    // Check if newParentId is a descendant of nodeId
    // If so, setting nodeId's parent to newParentId would create a cycle
    const descendants = await this.getDescendants(nodeId)
    const descendantIds = new Set(descendants.map(d => d['id'] as string))

    if (descendantIds.has(newParentId)) {
      throw new this.errors.ValidationError(
        'Cannot set parent: would create a circular reference in the tree hierarchy',
        [{ path: 'parent_id', message: 'Circular reference detected' }]
      )
    }
  }

  // ============================================================================
  // Hook overrides for validation
  // ============================================================================

  /**
   * Validate parent exists before creating a node
   */
  protected override async internalBeforeCreate(data: Partial<T>): Promise<Partial<T>> {
    const record = data as Record<string, unknown>
    const parentId = record['parent_id'] as string | null | undefined

    // If parent_id is set, validate it exists
    if (parentId) {
      const parent = await this.findById(parentId)
      if (!parent) {
        throw new this.errors.ValidationError(
          `Parent node '${parentId}' does not exist`,
          [{ path: 'parent_id', message: 'Parent node not found' }]
        )
      }
    }

    return this.sanitizeForeignKeys(data)
  }

  /**
   * Validate parent change doesn't create cycle before updating
   */
  protected override async internalBeforeUpdate(id: string, data: Partial<T>): Promise<Partial<T>> {
    const record = data as Record<string, unknown>

    // Only validate if parent_id is being changed
    if ('parent_id' in record) {
      const newParentId = record['parent_id'] as string | null

      if (newParentId) {
        // Validate parent exists
        const parent = await this.findById(newParentId)
        if (!parent) {
          throw new this.errors.ValidationError(
            `Parent node '${newParentId}' does not exist`,
            [{ path: 'parent_id', message: 'Parent node not found' }]
          )
        }

        // Validate no cycle would be created
        await this.validateNoCycle(id, newParentId)
      }
    }

    return this.sanitizeForeignKeys(data)
  }

  // ============================================================================
  // Seed
  // ============================================================================

  /**
   * Seed initial tree data from definition.
   * Supports both flat format (with parent_id) and nested format (with children).
   * Inserts nodes that don't exist yet (matched by ID).
   *
   * @returns Number of nodes seeded
   */
  override async seed(): Promise<number> {
    const seedData = await loadSeedData(this.treeDefinition.seed, this.logger, 'tree')
    if (!seedData || seedData.length === 0) {
      return 0
    }

    // Flatten nested structure if needed
    const flatData = flattenTreeSeed(seedData)

    let seeded = 0

    for (const item of flatData) {
      const id = item['id'] as string | undefined
      if (!id) continue

      const exists = await this.findById(id)
      if (!exists) {
        const data = { ...item, id } as Record<string, unknown>

        if (this.definition.timestamps) {
          const now = this.getNowTimestamp()
          data['created_at'] = now
          data['updated_at'] = now
        }

        await this.db(this.table).insert(this.serializeForDb(data))
        seeded++
      }
    }

    this.logger.info(`Tree data seeded: ${this.table} (${seeded} items)`)
    return seeded
  }

}

// ============================================================================
// Seed flattening utilities
// ============================================================================

interface TreeSeedNode {
  id: string
  children?: TreeSeedNode[]
  [key: string]: unknown
}

/**
 * Flatten nested tree seed data to flat records with parent_id.
 * Handles both flat (already has parent_id) and nested (has children) formats.
 *
 * @param nodes - Array of tree nodes (potentially nested)
 * @param parentId - Parent ID for current level (null for roots)
 * @returns Flat array of records with parent_id set
 */
function flattenTreeSeed(
  nodes: Record<string, unknown>[],
  parentId: string | null = null
): Record<string, unknown>[] {
  const result: Record<string, unknown>[] = []

  for (const node of nodes) {
    const { children, ...record } = node as TreeSeedNode

    // Set parent_id (don't override if already set in flat format)
    if (!('parent_id' in record)) {
      record['parent_id'] = parentId
    }

    result.push(record)

    // Recursively process children
    if (children && Array.isArray(children) && children.length > 0) {
      const childRecords = flattenTreeSeed(children as Record<string, unknown>[], record['id'] as string)
      result.push(...childRecords)
    }
  }

  return result
}

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Inject parent_id field into tree entity definition if not already present
 */
function injectParentIdField(definition: TreeEntityDefinition): TreeEntityDefinition {
  // Skip if parent_id already defined
  if (definition.fields['parent_id']) {
    return definition
  }

  const parentIdField: FieldDefinition = {
    label: { en: 'Parent', es: 'Padre' },
    input: 'select',
    db: {
      type: 'string',
      size: 26,
      nullable: true,
      index: true
    },
    relation: {
      table: definition.table,
      column: 'id',
      onDelete: 'SET NULL'
    },
    options: {
      endpoint: `/${definition.routePrefix ?? definition.table.replace(/_/g, '-')}`,
      valueField: 'id',
      labelField: definition.labelField
    },
    meta: {
      showInForm: true,
      showInDisplay: false
    }
  }

  return {
    ...definition,
    fields: {
      parent_id: parentIdField,
      ...definition.fields
    },
    indexes: [
      ...(definition.indexes ?? []),
      { columns: ['parent_id'] }
    ]
  }
}
